import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import TwoPaneLayout from "@/components/TwoPaneLayout";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import {
  fetchOutreach,
  fetchOutreachDetail,
  fetchOutreachSender,
  type OutreachSender,
  updateOutreachDraft,
  sendOutreach,
  deleteOutreach,
  type OutreachDetail,
  type OutreachListResponse,
} from "@/services/outreach";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Info,
  Mail,
  MailOpen,
  Send,
  ExternalLink,
  Trash2,
  Search,
  X,
  ChevronDown,
  ChevronUp,
  Pencil,
  Eye,
  MousePointerClick,
  Sparkles,
  UserCheck,
  Zap,
} from "lucide-react";
import { useEndUser } from "@/contexts/EndUserContext";
import OutreachSenderPanel from "@/components/outreach/OutreachSenderPanel";
import {
  trackOutreachEmailSent,
  trackOutreachDraftSaved,
  trackOutreachDraftDeleted,
  trackOutreachDraftEdited,
  trackOutreachListViewed,
} from "@/analytics/analytics";
import { PAGE_TITLE_CLASS } from "@/constants/design";

const PLACEHOLDER_RE = /\{\{[^}]*\}\}/g;

function findPlaceholders(text: string): string[] {
  return text.match(PLACEHOLDER_RE) || [];
}

function cleanPlaceholder(ph: string): string {
  return ph.replace(/^\{\{\s*/, "").replace(/\s*\}\}$/, "");
}

function statusBadge(status: string) {
  switch (status) {
    case "drafted":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
          Draft
        </span>
      );
    case "sent":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Sent
        </span>
      );
    case "delivered":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Delivered
        </span>
      );
    case "opened":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
          Opened
        </span>
      );
    case "clicked":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          Clicked
        </span>
      );
    case "bounced":
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Bounced
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
          {status}
        </span>
      );
  }
}

function renderBodyWithHighlights(body: string) {
  const parts = body.split(PLACEHOLDER_RE);
  const matches = body.match(PLACEHOLDER_RE) || [];
  const result: React.ReactNode[] = [];

  parts.forEach((part, i) => {
    if (part) result.push(<span key={`t-${i}`}>{part}</span>);
    if (i < matches.length) {
      const ph = matches[i];
      const label = cleanPlaceholder(ph);
      result.push(
        <Tooltip key={`ph-${i}`}>
          <TooltipTrigger asChild>
            <Link
              to="/profile"
              className="inline-flex items-center rounded bg-amber-100 border border-amber-400 px-1 py-0.5 text-sm font-bold text-amber-900 cursor-pointer hover:bg-amber-200 transition-colors"
            >
              {ph}
            </Link>
          </TooltipTrigger>
          <TooltipContent>Click to add your {label} in profile</TooltipContent>
        </Tooltip>
      );
    }
  });

  return result;
}

const Outreach = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { playerSummary, isSidebarOpen, closeSidebar, chatHistory, userType } = useEndUser();
  const isLinked = playerSummary.isLinked;

  // Outreach state
  const [listData, setListData] = useState<OutreachListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get("id"));
  const [detail, setDetail] = useState<OutreachDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit state
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editCoachEmail, setEditCoachEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [emailEditMode, setEmailEditMode] = useState<"preview" | "edit">("preview");

  // Filter/Sort/Search state
  const [filterStatus, setFilterStatus] = useState<string>(() => {
    try {
      return localStorage.getItem("outreach_filter_status") || "all";
    } catch {
      return "all";
    }
  });
  const [sortBy, setSortBy] = useState<string>(() => {
    try {
      return localStorage.getItem("outreach_sort_by") || "date_newest";
    } catch {
      return "date_newest";
    }
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // How-to-send collapsed state
  const [howToCollapsed, setHowToCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("outreach_howto_collapsed") === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    trackOutreachListViewed();
  }, []);

  // How coach emails go out: From name/address and reply-to (ZON-291)
  const [sender, setSender] = useState<OutreachSender | null>(null);
  useEffect(() => {
    if (userType === "coach") return;
    let cancelled = false;
    fetchOutreachSender()
      .then(result => {
        if (!cancelled) setSender(result);
      })
      .catch(error => console.error("Failed to load sender details:", error));
    return () => {
      cancelled = true;
    };
  }, [userType]);
  const replyToEmail = sender?.replyTo || "";

  // Persist filter/sort to localStorage
  useEffect(() => {
    try {
      localStorage.setItem("outreach_filter_status", filterStatus);
    } catch {}
  }, [filterStatus]);

  useEffect(() => {
    try {
      localStorage.setItem("outreach_sort_by", sortBy);
    } catch {}
  }, [sortBy]);

  // Search debounce
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  // Computed filtered/sorted list
  const filteredOutreach = useMemo(() => {
    let items = listData?.outreach || [];

    // Filter by status
    if (filterStatus !== "all") {
      items = items.filter(item => {
        switch (filterStatus) {
          case "draft":
            return item.status === "drafted";
          case "sent":
            return ["sent", "delivered", "opened"].includes(item.status);
          case "replied":
            return item.status === "replied";
          default:
            return true;
        }
      });
    }

    // Filter by search
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      items = items.filter(
        item =>
          (item.recipient.coachName || "").toLowerCase().includes(q) ||
          (item.recipient.schoolName || "").toLowerCase().includes(q) ||
          (item.email.subject || "").toLowerCase().includes(q)
      );
    }

    // Sort
    const sorted = [...items];
    switch (sortBy) {
      case "date_newest":
        sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case "date_oldest":
        sorted.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      case "school_az":
        sorted.sort((a, b) =>
          (a.recipient.schoolName || "").localeCompare(b.recipient.schoolName || "")
        );
        break;
      case "coach_az":
        sorted.sort((a, b) =>
          (a.recipient.coachName || "").localeCompare(b.recipient.coachName || "")
        );
        break;
    }

    return sorted;
  }, [listData?.outreach, filterStatus, debouncedSearch, sortBy]);

  // Read the active coach profile from sessionStorage (written by Index.tsx).
  // For players / admins this will be null and the backend handles scoping via the auth cookie.
  const linkedJgsProfile = useMemo(() => {
    try {
      return sessionStorage.getItem("recruit_ai_coach_active_profile") || undefined;
    } catch {
      return undefined;
    }
  }, []);

  // Load outreach list on mount
  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load detail when selectedId changes
  useEffect(() => {
    if (selectedId) {
      loadDetail(selectedId);
      setSearchParams({ id: selectedId }, { replace: true });
    } else {
      setDetail(null);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  async function loadList() {
    try {
      setLoading(true);
      const data = await fetchOutreach({ linkedJgsProfile });
      setListData(data);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load outreach");
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(id: string) {
    try {
      setDetailLoading(true);
      const doc = await fetchOutreachDetail(id, linkedJgsProfile);
      setDetail(doc);
      setEditSubject(doc.email.subject);
      setEditBody(doc.email.body);
      setEditCoachEmail(doc.recipient.coachEmail || "");
    } catch (err: any) {
      toast.error(err?.message || "Failed to load outreach detail");
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleSave() {
    if (!detail) return;
    try {
      setSaving(true);
      const updated = await updateOutreachDraft(
        detail._id,
        {
          subject: editSubject,
          body: editBody,
          coachEmail: editCoachEmail,
        },
        linkedJgsProfile
      );
      trackOutreachDraftSaved({ outreachId: detail._id });
      trackOutreachDraftEdited({ outreachId: detail._id });
      setDetail(updated);
      setEmailEditMode("preview");
      toast.success("Draft saved");
      loadList();
    } catch (err: any) {
      toast.error(err?.message || "Failed to save draft");
    } finally {
      setSaving(false);
    }
  }

  async function handleSend() {
    if (!detail) return;
    const remaining = [...findPlaceholders(editSubject), ...findPlaceholders(editBody)];
    if (remaining.length > 0) {
      toast.error(`Fill in all placeholders before sending: ${remaining.join(", ")}`);
      return;
    }
    try {
      setSending(true);
      const updated = await sendOutreach(detail._id, linkedJgsProfile);
      trackOutreachEmailSent({ outreachId: detail._id });
      setDetail(updated);
      toast.success("Email sent!");
      loadList();
    } catch (err: any) {
      toast.error(err?.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      setDeleting(true);
      await deleteOutreach(id, linkedJgsProfile);
      trackOutreachDraftDeleted({ outreachId: id });
      toast.success("Draft deleted");
      if (listData) {
        setListData({
          ...listData,
          outreach: listData.outreach.filter(o => o._id !== id),
          total: listData.total - 1,
        });
      }
      if (selectedId === id) {
        setSelectedId(null);
        setDetail(null);
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete draft");
    } finally {
      setDeleting(false);
    }
  }

  function toggleHowTo() {
    const next = !howToCollapsed;
    setHowToCollapsed(next);
    try {
      localStorage.setItem("outreach_howto_collapsed", String(next));
    } catch {}
  }

  const isDraft = detail?.status === "drafted";
  const hasChanges =
    detail &&
    (editSubject !== detail.email.subject ||
      editBody !== detail.email.body ||
      editCoachEmail !== (detail.recipient.coachEmail || ""));
  const placeholders = isDraft
    ? [...findPlaceholders(editSubject), ...findPlaceholders(editBody)]
    : [];
  const hasPlaceholders = placeholders.length > 0;

  // Placeholder helpers for the current detail body (read-only view highlighting)
  const bodyHasPlaceholders = detail ? findPlaceholders(detail.email.body).length > 0 : false;
  const editBodyHasPlaceholders = findPlaceholders(editBody).length > 0;

  return (
    <TwoPaneLayout
      isSidebarOpen={isSidebarOpen}
      onCloseSidebar={closeSidebar}
      sidebar={
        <Sidebar
          chatHistory={chatHistory}
          onNewChat={() => navigate("/chat")}
          onClose={closeSidebar}
          onSelectChat={id => navigate(`/chat?thread=${id}`)}
          playerName={playerSummary.playerName}
          classYear={playerSummary.classYear}
          jgsRank={playerSummary.jgsRank}
          scoringDifferential={playerSummary.scoringDifferential}
          isLinked={isLinked}
        />
      }
      main={
        <div className="relative flex-1 overflow-y-auto">
          {selectedId && detail ? (
            // Detail view
            <div className="max-w-3xl mx-auto p-6">
              <button
                onClick={() => setSelectedId(null)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to list
              </button>

              {detailLoading ? (
                <div className="flex justify-center py-12">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Recipient info */}
                  <div className="rounded-lg border border-border bg-card p-4 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-semibold text-foreground">
                        {detail.recipient.schoolName || "Unknown School"}
                      </h2>
                      {statusBadge(detail.status)}
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1.5">
                      {detail.recipient.coachName && <p>Coach: {detail.recipient.coachName}</p>}
                      <div className="flex items-center gap-2">
                        <span className="shrink-0">Email:</span>
                        {isDraft ? (
                          <Input
                            type="email"
                            value={editCoachEmail}
                            onChange={e => setEditCoachEmail(e.target.value)}
                            placeholder="coach@school.edu"
                            className="h-7 text-sm bg-background max-w-xs"
                          />
                        ) : (
                          <span>{detail.recipient.coachEmail || "—"}</span>
                        )}
                      </div>
                      {detail.recipient.division && <p>Division: {detail.recipient.division}</p>}
                    </div>
                    {detail.chatThreadId && (
                      <button
                        onClick={() => navigate(`/chat?thread=${detail.chatThreadId}`)}
                        className="flex items-center gap-1 text-sm text-primary-text hover:underline mt-1"
                      >
                        <ExternalLink className="h-3 w-3" />
                        View Conversation
                      </button>
                    )}
                  </div>

                  {/* Collapsible "How to send" — above email form */}
                  {isDraft && (
                    <div className="rounded-lg border border-border bg-card overflow-hidden">
                      <button
                        type="button"
                        onClick={toggleHowTo}
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                      >
                        <h3 className="text-sm font-semibold text-foreground">How to send</h3>
                        {howToCollapsed ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                      {!howToCollapsed && (
                        <div className="px-4 pb-4 space-y-3 text-sm text-muted-foreground border-t border-border pt-3">
                          {userType === "coach" ? (
                            <>
                              <div className="flex items-start gap-2.5">
                                <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                                <p>
                                  <span className="font-medium text-foreground">Coach mode</span> —
                                  you can review, edit, and regenerate this draft. When it's ready,
                                  the student will send it from their own account so the email comes
                                  from them.
                                </p>
                              </div>
                              <div className="flex items-start gap-2.5">
                                <Mail className="h-4 w-4 text-primary-text mt-0.5 shrink-0" />
                                <p>
                                  <span className="font-medium text-foreground">Copy & paste</span>{" "}
                                  — you can also copy the subject and body below to share with the
                                  student directly.
                                </p>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-start gap-2.5">
                                <Send className="h-4 w-4 text-primary-text mt-0.5 shrink-0" />
                                <p>
                                  <span className="font-medium text-foreground">
                                    Send from here
                                  </span>{" "}
                                  — click "Send Email" below and we'll deliver it for you. The email
                                  will still come from your account, and any replies from coaches go
                                  straight to{" "}
                                  {replyToEmail ? (
                                    <span className="font-medium text-foreground">
                                      {replyToEmail}
                                    </span>
                                  ) : (
                                    "your inbox"
                                  )}{" "}
                                  — not to golfrecruit.ai. You can change this address on your{" "}
                                  <Link to="/profile?field=coachReplyEmail" className="underline">
                                    profile
                                  </Link>
                                  . We'll also track when coaches open the email and click links so
                                  you can see who's engaging.
                                </p>
                              </div>
                              <div className="flex items-start gap-2.5">
                                <Mail className="h-4 w-4 text-primary-text mt-0.5 shrink-0" />
                                <p>
                                  <span className="font-medium text-foreground">Copy & paste</span>{" "}
                                  — prefer to use your own email client? Copy the subject and body
                                  below, paste them into a new message, and send it yourself. You
                                  won't get open or click tracking, but the email is all yours.
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Engagement */}
                  {detail.sentAt && (
                    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                      <h3 className="text-sm font-semibold text-foreground">Engagement</h3>

                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Send className="h-4 w-4 shrink-0" />
                        <span>
                          Sent on {new Date(detail.sentAt).toLocaleDateString()} at{" "}
                          {new Date(detail.sentAt).toLocaleTimeString()}
                        </span>
                      </div>

                      {detail.firstOpenedAt && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MailOpen className="h-4 w-4 shrink-0 text-emerald-600" />
                          <span>
                            Opened on {new Date(detail.firstOpenedAt).toLocaleDateString()} at{" "}
                            {new Date(detail.firstOpenedAt).toLocaleTimeString()}
                            {(detail.opens?.length ?? 0) > 1 && (
                              <span className="text-muted-foreground/70">
                                {" "}
                                ({detail.opens!.length} total opens)
                              </span>
                            )}
                          </span>
                        </div>
                      )}

                      {(detail.clicks?.length ?? 0) > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium text-purple-800">
                            <MousePointerClick className="h-4 w-4 shrink-0 text-purple-600" />
                            <span>Clicks ({detail.clicks!.length})</span>
                          </div>
                          <div className="ml-6 space-y-1.5">
                            {detail.clicks!.map((click, i) => (
                              <div key={i} className="flex items-start gap-2 text-sm">
                                <span className="text-muted-foreground whitespace-nowrap shrink-0">
                                  {new Date(click.timestamp).toLocaleDateString()}{" "}
                                  {new Date(click.timestamp).toLocaleTimeString()}
                                </span>
                                {click.url && (
                                  <a
                                    href={click.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-text hover:underline truncate"
                                  >
                                    {click.url}
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!detail.firstOpenedAt && !detail.clicks?.length && (
                        <p className="text-sm text-muted-foreground/70 ml-6">No engagement yet</p>
                      )}
                    </div>
                  )}

                  {/* Email form */}
                  <div className="rounded-lg border border-border bg-card">
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-foreground">
                        {isDraft ? "Email" : "Email Content"}
                      </h3>
                      {isDraft && (
                        <button
                          type="button"
                          onClick={() =>
                            setEmailEditMode(emailEditMode === "edit" ? "preview" : "edit")
                          }
                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {emailEditMode === "edit" ? (
                            <>
                              <Eye className="h-3.5 w-3.5" />
                              Preview
                            </>
                          ) : (
                            <>
                              <Pencil className="h-3.5 w-3.5" />
                              Edit
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    <div className="p-4 space-y-4">
                      {isDraft && userType !== "coach" && sender && (
                        <OutreachSenderPanel sender={sender} />
                      )}
                      <div>
                        <label className="text-xs font-medium uppercase tracking-wide text-foreground/60 mb-1.5 block">
                          Subject
                        </label>
                        {isDraft && emailEditMode === "edit" ? (
                          <Input
                            value={editSubject}
                            onChange={e => setEditSubject(e.target.value)}
                            className="bg-background"
                          />
                        ) : (
                          <p className="text-sm text-foreground bg-muted/40 border border-border/50 rounded-md px-3 py-2">
                            {isDraft ? editSubject : detail.email.subject}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="text-xs font-medium uppercase tracking-wide text-foreground/60 mb-1.5 block">
                          Body
                        </label>
                        {isDraft && emailEditMode === "edit" ? (
                          <Textarea
                            value={editBody}
                            onChange={e => setEditBody(e.target.value)}
                            className="min-h-[240px] resize-y bg-background"
                          />
                        ) : (
                          <div className="text-sm text-foreground bg-muted/40 border border-border/50 rounded-md px-3 py-2 whitespace-pre-wrap min-h-[120px]">
                            {(isDraft ? editBodyHasPlaceholders : bodyHasPlaceholders)
                              ? renderBodyWithHighlights(isDraft ? editBody : detail.email.body)
                              : isDraft
                                ? editBody
                                : detail.email.body}
                          </div>
                        )}
                      </div>

                      {/* Profile tip */}
                      {isDraft && userType !== "coach" && (
                        <div className="flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                          <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                          <p className="text-sm text-blue-800">
                            Keep your{" "}
                            <Link
                              to="/profile"
                              className="font-medium underline hover:text-blue-900"
                            >
                              player profile
                            </Link>{" "}
                            up to date so future emails automatically include your latest stats,
                            achievements, and contact info.
                          </p>
                        </div>
                      )}

                      {hasPlaceholders && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
                          <p className="text-sm font-medium text-amber-800 mb-2">
                            Fill in all placeholders before sending
                          </p>
                          <div className="grid grid-cols-2 gap-1.5">
                            {placeholders.map((ph, i) => (
                              <div key={i} className="flex items-center gap-1.5">
                                <X className="h-3.5 w-3.5 text-amber-700 shrink-0" />
                                <span className="text-xs font-medium text-amber-900">
                                  {cleanPlaceholder(ph)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions inside card footer */}
                    {isDraft && (
                      <div className="px-4 py-3 border-t border-border bg-muted/30 rounded-b-lg">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full sm:w-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                                disabled={deleting}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                {deleting ? "Deleting..." : "Delete"}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete this draft?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete the draft email to{" "}
                                  {detail.recipient.coachName || "the coach"} at{" "}
                                  {detail.recipient.schoolName || "Unknown School"}. This action
                                  cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-red-600 hover:bg-red-700"
                                  onClick={() => handleDelete(detail._id)}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>

                          <Button
                            variant="outline"
                            className="w-full sm:w-auto"
                            onClick={handleSave}
                            disabled={saving || !hasChanges}
                          >
                            {saving ? "Saving..." : "Save Changes"}
                          </Button>

                          {userType !== "coach" && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  className="w-full sm:w-auto"
                                  disabled={
                                    sending ||
                                    !editCoachEmail.trim() ||
                                    hasPlaceholders ||
                                    emailEditMode === "edit"
                                  }
                                >
                                  <Send className="h-4 w-4 mr-2" />
                                  {sending ? "Sending..." : "Send Email"}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Send this email?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will send the email to{" "}
                                    {detail.recipient.coachName || "the coach"} at {editCoachEmail}
                                    {sender
                                      ? ` from ${sender.fromName}. Replies will go to ${sender.replyTo}.`
                                      : "."}{" "}
                                    This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={handleSend}>Send</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>

                        {userType === "coach" && (
                          <p className="text-sm text-blue-600 mt-2">
                            Only the student can send this email from their own account.
                          </p>
                        )}

                        {userType !== "coach" && !editCoachEmail.trim() && (
                          <p className="text-sm text-amber-600 mt-2">
                            Coach email is missing — cannot send
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // List view
            <div className="max-w-3xl mx-auto p-6">
              <div className="mb-6">
                <h1 className={PAGE_TITLE_CLASS}>Coach Emails</h1>
              </div>

              {/* Filter/Sort/Search bar */}
              {(listData?.outreach?.length ?? 0) > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <div className="relative flex-1 min-w-[180px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search coaches, schools, subjects…"
                      className="pl-8 pr-8 h-9 bg-background"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[120px] h-9">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="replied">Replied</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-[150px] h-9">
                      <SelectValue placeholder="Sort" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date_newest">Date (newest)</SelectItem>
                      <SelectItem value="date_oldest">Date (oldest)</SelectItem>
                      <SelectItem value="school_az">School (A-Z)</SelectItem>
                      <SelectItem value="coach_az">Coach (A-Z)</SelectItem>
                    </SelectContent>
                  </Select>

                  <span className="text-xs text-muted-foreground whitespace-nowrap ml-auto">
                    Showing: {filteredOutreach.length} email
                    {filteredOutreach.length !== 1 ? "s" : ""}
                  </span>
                </div>
              )}

              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : !listData?.outreach?.length ? (
                <div className="py-12 space-y-8">
                  <div className="text-center space-y-2">
                    <Mail className="h-12 w-12 text-primary-text mx-auto mb-3" />
                    <h2 className="text-xl font-semibold text-foreground">
                      Reach coaches directly — with emails that stand out
                    </h2>
                  </div>

                  <div className="max-w-md mx-auto space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Sparkles className="h-4 w-4 text-primary-text" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          Personalized, not generic
                        </p>
                        <p className="text-sm text-muted-foreground">
                          golfrecruit.ai drafts emails using your actual stats, the coach's name,
                          and program-specific details. Not a template.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <UserCheck className="h-4 w-4 text-primary-text" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">Know who's interested</p>
                        <p className="text-sm text-muted-foreground">
                          Track when coaches open your email and click your links. See which
                          programs are paying attention.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Zap className="h-4 w-4 text-primary-text" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">One click to send</p>
                        <p className="text-sm text-muted-foreground">
                          Review the AI-drafted email, make any edits, and send. Emails go directly
                          from your name.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Button
                      onClick={() =>
                        navigate("/chat", {
                          state: {
                            autoPrompt:
                              "Help me draft a personalized introductory email to a college golf coach. Use my profile data and make it ready to review and send.",
                          },
                        })
                      }
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Draft my first email
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() =>
                        navigate("/chat", {
                          state: {
                            autoPrompt:
                              "Explain how coach outreach works in college golf recruiting. What's the best strategy for reaching out to coaches, and how does golfrecruit.ai help with the process?",
                          },
                        })
                      }
                    >
                      Learn more about outreach
                    </Button>
                  </div>
                </div>
              ) : filteredOutreach.length === 0 ? (
                <div className="text-center py-12">
                  <Search className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <h2 className="text-base font-semibold mb-1">No results</h2>
                  <p className="text-sm text-muted-foreground">
                    No emails match your current filters.{" "}
                    <button
                      type="button"
                      className="text-primary-text hover:underline"
                      onClick={() => {
                        setFilterStatus("all");
                        setSearchQuery("");
                      }}
                    >
                      Clear filters
                    </button>
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredOutreach.map(item => (
                    <div key={item._id} className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedId(item._id)}
                        className="flex-1 min-w-0 text-left bg-card border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm truncate">
                                {item.recipient.coachName || "Coach"} —{" "}
                                {item.recipient.schoolName || "Unknown School"}
                              </span>
                              {statusBadge(item.status)}
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {item.email.subject}
                            </p>
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </button>
                      {item.status === "drafted" && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              type="button"
                              onClick={e => e.stopPropagation()}
                              className="shrink-0 p-2 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
                              aria-label="Delete draft"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this draft?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete the draft email to{" "}
                                {item.recipient.coachName || "the coach"} at{" "}
                                {item.recipient.schoolName || "Unknown School"}. This action cannot
                                be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-red-600 hover:bg-red-700"
                                onClick={() => handleDelete(item._id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      }
    />
  );
};

export default Outreach;
